import { useState } from 'react';

import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  FolderOpen,
  HelpCircle,
  Settings,
  UserCircle,
  Users,
} from 'lucide-react';
import { useLocation } from 'react-router';

import { useCurrentUser, useEntitlements } from '@chartai/api-client';

import { cn } from '@/lib/utils';
import { UnderConstructionDialog } from '@/shared/components/UnderConstructionDialog';
import { useSidebarStore } from '@/shared/stores/sidebarStore';

import { SidebarItem } from './SidebarItem';

interface SidebarProps {
  needsReviewCount?: number;
}

export function Sidebar({ needsReviewCount = 0 }: SidebarProps) {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const location = useLocation();
  // When navigating across feature boundaries (e.g. patient → chart detail),
  // the originating page stamps fromPath in router state so the sidebar keeps
  // the correct module highlighted rather than switching to the new URL's module.
  const fromPath = (location.state as { fromPath?: string } | null)?.fromPath ?? '';

  const [constructionFeature, setConstructionFeature] = useState<string | null>(null);

  // CAB-604: Documents is Pro+. The item stays and stays navigable — the page
  // itself carries the upgrade prompt. Only flag it once entitlements resolve,
  // so a paying user never sees a lock flash on first paint.
  const { user } = useCurrentUser();
  const { can, isLoading: entitlementsLoading } = useEntitlements(user?.uid ?? '');
  const documentsLocked = !entitlementsLoading && !can('clinicalDocuments');

  return (
    <aside
      className={cn(
        'relative flex shrink-0 flex-col overflow-visible bg-[var(--wf-nav)] text-white transition-all duration-200',
        collapsed ? 'w-16' : 'w-48',
      )}
    >
      {/* Toggle button — positioned at the right edge, aligned with first nav item */}
      <button
        type="button"
        onClick={toggle}
        className="absolute top-3 -right-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--wf-nav)]/50 bg-[var(--wf-nav)] text-white shadow-md transition-colors hover:bg-[var(--wf-nav)]/80"
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>

      {/* Navigation */}
      <nav aria-label="Dashboard navigation" className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex-1 space-y-0.5 px-2">
          {/* Main nav */}
          <SidebarItem
            icon={Calendar}
            label="Today's Appointments"
            to="/dashboard/appointments"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
          <SidebarItem
            icon={Users}
            label="Patients"
            to="/dashboard/patients"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
          <SidebarItem
            icon={ClipboardCheck}
            label="Ready for Review"
            to="/dashboard/review-queue"
            collapsed={collapsed}
            badge={needsReviewCount}
          />

          {/* WORKFLOW section */}
          <div className="mx-2 mt-3 border-t border-white/10 pt-2" />
          {!collapsed && (
            <p className="px-3 pb-1 text-[10px] font-semibold tracking-widest text-[var(--wf-muted)] uppercase">
              Workflow
            </p>
          )}
          <SidebarItem
            icon={FileText}
            label="Templates"
            to="/dashboard/templates"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
          <SidebarItem
            icon={FolderOpen}
            label="Documents"
            to="/dashboard/documents"
            collapsed={collapsed}
            activeOverridePath={fromPath}
            locked={documentsLocked}
          />

          {/* ACCOUNT section */}
          <div className="mx-2 mt-3 border-t border-white/10 pt-2" />
          {!collapsed && (
            <p className="px-3 pb-1 text-[10px] font-semibold tracking-widest text-[var(--wf-muted)] uppercase">
              Account
            </p>
          )}
          <SidebarItem
            icon={UserCircle}
            label="Profiles"
            to="/dashboard/profiles"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
          <SidebarItem
            icon={HelpCircle}
            label="Help / Support"
            to="/dashboard/support"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
          <SidebarItem
            icon={Settings}
            label="Settings"
            to="/dashboard/settings"
            collapsed={collapsed}
            activeOverridePath={fromPath}
          />
        </div>

        {/* Logged-in user info + logout — hidden for now */}
      </nav>

      {/* Under Construction Dialog */}
      <UnderConstructionDialog
        open={constructionFeature !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConstructionFeature(null);
          }
        }}
        featureName={constructionFeature ?? ''}
      />
    </aside>
  );
}
